import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { FormControl, InputGroup } from 'react-bootstrap';
import { FloatingLabel } from './FloatingLabel';
import PropTypes from 'prop-types';
import Label from './Label';
import { Validator } from './ValidationResult';
import { makeLabel } from '../utils/generalUtils';
import { useAutofillNotifier } from '../hooks/autofillHook';
import { useMemo } from 'react';
import { withComponentCapabilities } from './capabilities/componentCapabilities';

export const customCtrlDef = {
    def: (t, tools) => ({
        form: [{
            borderless: true,
            rows: [
                ...tools.labelFields(t),
                ...tools.fieldFields(t),
                [
                    { type: "number", min: 1, step: 1, field: "maxlength", label: t('maxLength') }
                ],
                ...tools.commonFields(t)
            ]
        }]
    }),
    control: ({ def, href, maxlength, ...props }) => <TextInput maxLength={def.maxlength ?? maxlength} href={href ?? def.href} {...props}></TextInput>
}

/**
 * Single line textbox.
 * @component
 */
const TextInput = withComponentCapabilities(forwardRef(({ value, live = false, onChange, hideLabel = false, placeholder, required = false, prependCtrl, prependText, appendCtrl, appendText, disabled = false, readonly = false, wildcard, href, ...props }, ref) => {
    const [text, setText] = useState(value ?? '');
    const [validity, setValidity] = useState();
    const { createAutofillProps } = useAutofillNotifier();
    const ctrlRef = useRef();

    const handleUpdate = useCallback(newText => {
        const valid = ctrlRef.current?.checkValidity();
        setValidity(valid);
        if ((valid || live) && (newText ?? '') !== (value ?? '')) {
            onChange(newText);
        }
    }, [value, live, onChange]);    

    const autofillProps = useMemo(() =>
        createAutofillProps((el) => {
            const newText = el.value;
            let shouldUpdate = false;

            setText(prev => {
                shouldUpdate = newText && prev !== newText;
                return newText;
            });

            if (shouldUpdate && (!required || newText)) {
                handleUpdate(newText);
                return true;
            }
            return shouldUpdate;
        }, ctrlRef)
    , [createAutofillProps, handleUpdate, required, value]);

    useImperativeHandle(ref, () => ctrlRef.current);

    useEffect(() => {
        setText(value ?? '')
        setValidity();
    }, [value]);

    const handleChange = useCallback(e => {
        const newText = e.target.value;
        setText(newText);
        if (live || document.activeElement !== e.target) {
            handleUpdate(newText)
        }
    }, [live, handleUpdate])

    if (prependCtrl && prependText) {
        throw new Error("Cannot specify both prependCtrl and prependText");
    }

    if (appendCtrl && appendText) {
        throw new Error("Cannot specify both appendCtrl and appendText");
    }

    let prepend = null;
    if (prependCtrl || prependText) {
        prepend = prependCtrl ?? <InputGroup.Text>{prependText}</InputGroup.Text>
    }

    let append = null;
    if (appendCtrl || appendText) {
        append = appendCtrl ?? <InputGroup.Text>{appendText}</InputGroup.Text>
    }

    const textInput = <FormControl {...autofillProps} isInvalid={validity === false} type="text" value={text} placeholder={makeLabel(placeholder, required)} required={required} onChange={handleChange} onBlur={() => handleUpdate(text)} onInvalid={() => setValidity(false)} disabled={disabled} {...props}></FormControl>

    return readonly ? <Label hideLabel={hideLabel} placeholder={placeholder} text={text || wildcard} href={href}></Label> : <FloatingLabel hideLabel={hideLabel} placeholder={placeholder ?? ''} required={required} value={text} href={href}>
            {prepend || append ?
                <InputGroup>
                    <Validator ref={ctrlRef} value={value} onValidation={setValidity}>
                        {prepend ? <InputGroup.Prepend>{prepend}</InputGroup.Prepend> : null}
                        {textInput}
                        {append ? <InputGroup.Append>{append}</InputGroup.Append> : null}
                    </Validator>
                </InputGroup>
                : <Validator ref={ctrlRef} value={value} onValidation={setValidity}>{textInput}</Validator>
            }
    </FloatingLabel>
}), {

});

TextInput.propTypes = {
    /**
     * The text to be displayed by the control
     */
    value: PropTypes.string,

    /**
     * Set to true for every key stroke to call the onChange function
     */
    live: PropTypes.bool,

    /**
     * Function to call when the value has been changed
     */
    onChange: PropTypes.func,

    /**
     * The label for the control
     */
    placeholder: PropTypes.string,

    /**
     * The control must have a value for the from in which it is contained to validate
     */
    required: PropTypes.bool,

    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,

    /**
     * Collapses the floating label used for spacing
     */
    hideLabel: PropTypes.bool,

    /**
     * The maximum number of characters that can be entered into this control
     */
    maxLength: PropTypes.number,

    /**
     * A control to prepend to the beginning of the text input. It is invalid to specify both prependCtrl and prependText
     */
    prependCtrl: PropTypes.node,

    /**
     * Text or icon to prepend to the beginning of the text input. It is invalid to specify both prependCtrl and prependText
     */
    prependText: PropTypes.node,

    /**
     * A control to append to the end of the text input. It is invalid to specify both appendCtrl and appendText
     */
    appendCtrl: PropTypes.node,

    /**
    * Text or icon to append to the end of the text input. It is invalid to specify both appendCtrl and appendText
    */
    appendText: PropTypes.node,

    /**
     * Render the control value as text rather an input control
     */
    readonly: PropTypes.bool,

    /**
     * An option navigation URL to be followed if the label text is clicked
     */
    href: PropTypes.string,
}

export default TextInput;